import { tx, txt, useContent } from "@/content/ContentContext";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { detectDialCountry, toE164 } from "@/lib/phone";
import { CountryCodePicker } from "@/components/CountryCodePicker";

const MAX = 2000;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;
const blank = (type, prefill, country) => ({ type, business_name: prefill.business_name || "", contact_person: "", phone_country: country, phone_number: "", email: prefill.email || "", message: "", website: "" });

function validate(f) {
  const e = {};
  ["business_name", "contact_person", "phone_number", "email", "message"].forEach((k) => !f[k].trim() && (e[k] = tx("contact.this_field_is_required")));
  if (!e.email && !EMAIL_RE.test(f.email.trim())) e.email = tx("contact.enter_a_valid_email");
  if (!e.phone_number && !toE164(f.phone_country, f.phone_number)) e.phone_number = tx("contact.enter_a_valid_mobile_number");
  if (f.message.length > MAX) e.message = tx("contact.message_too_long", { max: MAX.toLocaleString("en") });
  return e;
}

const inp = (err) => `h-11 w-full rounded-xl border bg-white px-3.5 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-slate-900 ${err ? "border-rose-400" : "border-slate-200"}`;

function Row({ label, err, id, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</span>
      {children}
      {err && <span className="block text-xs font-semibold text-rose-600" data-testid={`contact-error-${id}`}>{err}</span>}
    </label>
  );
}

export function ContactForm({ initialType = "support" }) {
  useContent();
  const { session } = useAuth();
  const owner = session && session.user?.role === "owner";
  const prefill = owner ? { business_name: session.business?.name, email: session.user?.email } : {};
  const [f, setF] = useState(() => blank(initialType, prefill, "PK"));
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  const [country, setCountry] = useState("PK");

  useEffect(() => {
    detectDialCountry().then((c) => { setCountry(c); setF((x) => (x.phone_number ? x : { ...x, phone_country: c })); });
  }, []);
  useEffect(() => {
    if (!owner) return;
    setF((x) => ({ ...x, business_name: x.business_name || prefill.business_name || "", email: x.email || prefill.email || "" }));
  }, [owner, prefill.business_name, prefill.email]);
  const set = (k) => (e) => { setF({ ...f, [k]: e?.target ? e.target.value : e }); setErr({ ...err, [k]: undefined }); setSent(""); };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate(f);
    setErr(v);
    if (Object.keys(v).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/contact", { ...f, source_page: window.location.pathname + window.location.search });
      setSent(data.message);
      toast.success(data.message);
      setF(blank(f.type, prefill, country));
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      if (d?.errors) setErr(d.errors);
      else toast.error(errMsg(ex));
      if (ex?.response?.status === 429) setErr({ form: errMsg(ex) });
    } finally {
      setBusy(false);
    }
  };
  const left = MAX - f.message.length;

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid="contact-form">
      <div className="flex gap-1 rounded-xl bg-slate-100 p-1" role="radiogroup" aria-label={tx("contact.enquiry_type")} data-testid="contact-type">
        {[["support", tx("contact.support")], ["sales", tx("contact.sales")]].map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={f.type === k} onClick={() => set("type")(k)} data-testid={`contact-type-${k}`}
            className={`h-10 flex-1 rounded-lg text-sm font-bold transition-colors ${f.type === k ? "bg-slate-950 text-white" : "text-slate-600 hover:text-slate-950"}`}>{l}</button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Row label={tx("contact.business_name")} err={err.business_name} id="business_name"><input className={inp(err.business_name)} value={f.business_name} onChange={set("business_name")} data-testid="contact-business-name" /></Row>
        <Row label={tx("contact.contact_person")} err={err.contact_person} id="contact_person"><input className={inp(err.contact_person)} value={f.contact_person} onChange={set("contact_person")} data-testid="contact-person" /></Row>
      </div>
      <Row label={tx("contact.mobile")} err={err.phone_number} id="phone_number">
        <div className="flex gap-2">
          <CountryCodePicker value={f.phone_country} onChange={set("phone_country")} />
          <input type="tel" inputMode="tel" className={inp(err.phone_number)} value={f.phone_number} onChange={set("phone_number")} placeholder="300 1234567" data-testid="contact-phone" />
        </div>
      </Row>
      <Row label={tx("contact.email")} err={err.email} id="email"><input type="email" className={inp(err.email)} value={f.email} onChange={set("email")} data-testid="contact-email" /></Row>
      <Row label={tx("contact.write_to_us")} err={err.message} id="message">
        <textarea className={`${inp(err.message)} h-32 resize-y py-2.5`} value={f.message} onChange={set("message")} data-testid="contact-message" />
        <span className={`block text-right font-mono text-[11px] ${left < 0 ? "font-bold text-rose-600" : "text-slate-400"}`} data-testid="contact-message-counter">{left.toLocaleString("en")} characters left</span>
      </Row>
      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" data-testid="contact-honeypot" />
      {err.form && <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="contact-form-error">{err.form}</div>}
      {sent && <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="contact-success"><CheckCircle2 className="h-4 w-4" />{sent}</div>}
      <button disabled={busy || left < 0} data-testid="contact-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-amber-500 font-bold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send message
      </button>
    </form>
  );
}
